import { ConferenceOptions, Reservation } from "twilio-taskrouter";
import type { Call, Device } from "@twilio/voice-sdk";
import type { VoiceCall } from "~/modules/VoiceCall";
import { VoiceCallImpl } from "~/modules/VoiceCall";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { getLogger } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextManager } from "~/modules/contextManager/ContextManager";
import {
    getDefaultCallerID,
    getDefaultQueueSid,
    getDefaultWorkflowSid,
    hasVoiceTaskWithStatus,
    isOutboundCallingEnabled,
    isOutboundCallPending,
    isWorkerOffline,
    sendTrackingEvent
} from "../../ActionUtils";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { VoiceControllerImpl } from "~/modules/voice/VoiceControllerImpl";
import { EVENTS } from "~/modules/analytics/Analytics";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { Action } from "~/modules/actions/Action";




export interface StartOutboundCallOptions {
    attributesForTaskCreation?: object;
    conferenceOptions?: ConferenceOptions;
    voiceDevice?: Device;
}






















export class StartOutboundCall implements Action<Promise<VoiceCall>> {
    


    #toNumber: string;

    


    #fromNumber?: string;

    


    #workflowSid?: string;

    


    #taskQueueSid?: string;

    


    #options?: StartOutboundCallOptions;

    constructor(
        toNumber: string,
        fromNumber?: string,
        workflowSid?: string,
        taskQueueSid?: string,
        options?: StartOutboundCallOptions
    ) {
        this.#toNumber = toNumber;
        this.#fromNumber = fromNumber;
        this.#workflowSid = workflowSid;
        this.#taskQueueSid = taskQueueSid;
        this.#options = options;
    }

    async run(ctx: {}): Promise<VoiceCall> {
        const context = ctx as ContextManager;
        const taskRouter = context.getInstanceOf(TaskRouterImpl);
        const logger = getLogger(context)("StartOutboundCall");
        const analytics = context.getInstanceOf(AnalyticsImpl);
        const accountConfig = context.getInstanceOf(AccountConfigDataContainer);
        const voiceController = context.getInstanceOf(VoiceControllerImpl);

        logger.debug(`startOutboundCall invoked to number: ${this.#toNumber}`);

        const worker = taskRouter.worker;
        const { attributesForTaskCreation, conferenceOptions } = this.#options || {};

        if (!worker) {
            const errorMsg = `startOutboundCall: worker is not initialized`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (!this.#toNumber) {
            const errorMsg = `startOutboundCall: toNumber is a required parameter`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const fromNumberParam = this.#fromNumber || getDefaultCallerID(accountConfig.get().outboundCallFlows);
        if (!fromNumberParam) {
            const errorMsg = `startOutboundCall: fromNumber is required`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const taskQueueSidParam = this.#taskQueueSid || getDefaultQueueSid(accountConfig.get().outboundCallFlows);
        if (!taskQueueSidParam) {
            const errorMsg = `startOutboundCall: taskQueueSid is required`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const workflowSidParam = this.#workflowSid || getDefaultWorkflowSid(accountConfig.get().outboundCallFlows);
        if (!workflowSidParam) {
            const errorMsg = `startOutboundCall: workflowSid is required`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const taskrouterOfflineActivitySid = accountConfig.get().taskrouterOfflineActivitySid;
        if (!taskrouterOfflineActivitySid) {
            const errorMsg = "startOutboundCall: taskrouterOfflineActivitySid is undefined";
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (isWorkerOffline(worker, taskrouterOfflineActivitySid)) {
            const errorMsg = `startOutboundCall: worker is offline, outbound call cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (!isOutboundCallingEnabled(accountConfig.get().outboundCallFlows)) {
            const errorMsg = `startOutboundCall: Outbound calling is disabled in Flex account configuration,
                                outbound call cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        
        if (isOutboundCallPending()) {
            const errorMsg = `startOutboundCall: Another outbound call is already pending, outbound call cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (hasVoiceTaskWithStatus(worker, "pending")) {
            const errorMsg = `startOutboundCall: Inbound call is pending, outbound call cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (hasVoiceTaskWithStatus(worker, "accepted")) {
            const errorMsg = `startOutboundCall: Another voice task is already in accepted status, outbound call cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        await this.#initVoiceController(context);

        if (!voiceController.isAudioInputDeviceAvailable()) {
            const errorMsg = `startOutboundCall: no audio input device, outbound call cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        
        return new Promise(async (resolve, reject) => {
            let timeoutId: number | NodeJS.Timeout;
            let taskSid: string;
            const handleIncomingCall = (call: Call) => {
                logger.info(
                    "Incoming call event received in startOutboundCall",
                    call?.parameters?.From,
                    call?.parameters?.To
                );
                if (call?.parameters?.From === fromNumberParam) {
                    logger.debug("From phone numbers match");

                    clearTimeout(timeoutId);
                    voiceController.unsubscribeFromIncomingCallEvent(handleIncomingCall);

                    const voiceCall = new VoiceCallImpl(context, call, voiceController.voiceDevice);

                    resolve(voiceCall);
                } else {
                    logger.error(
                        "Incoming call is coming from a different number, not from the one given as an argument to startOutboundCall action"
                    );
                }
            };

            voiceController.subscribeToIncomingCallEvent(handleIncomingCall);

            let handleReservationFailed: (reservation: Reservation) => void;

            const handleReservationCreated = async (reservation: Reservation) => {
                logger.info("A reservation has been created for the task", reservation?.task?.sid);
                if (reservation.task.sid === taskSid) {
                    try {
                        const conferenceCreated = await reservation.conference(conferenceOptions);
                        logger.debug("Conference created", conferenceCreated);
                    } catch (error) {
                        logger.error("Error creating conference", error);
                    } finally {
                        worker.off("reservationCreated", handleReservationCreated);
                        worker.off("reservationFailed", handleReservationFailed);
                    }
                }
            };

            handleReservationFailed = (reservation: Reservation) => {
                logger.error("Failed to create reservation for task:", reservation?.task?.sid);
                if (reservation.task.sid === taskSid) {
                    worker.off("reservationCreated", handleReservationCreated);
                    worker.off("reservationFailed", handleReservationFailed);
                }
            };

            timeoutId = setTimeout(() => {
                voiceController.unsubscribeFromIncomingCallEvent(handleIncomingCall);
                worker.off("reservationCreated", handleReservationCreated);
                worker.off("reservationFailed", handleReservationFailed);
                const errorMsg = `Timeout: No incoming call event received within 30 seconds`;
                logger.error(errorMsg);
                reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
            }, 30000);
            worker.on("reservationCreated", handleReservationCreated);
            worker.on("reservationFailed", handleReservationFailed);

            try {
                const outboundAttributes = { ...attributesForTaskCreation, direction: "outbound" };
                const taskOptions = {
                    taskChannelUniqueName: "voice",
                    attributes: outboundAttributes
                };

                taskSid = await worker.createTask(
                    this.#toNumber,
                    fromNumberParam,
                    workflowSidParam,
                    taskQueueSidParam,
                    taskOptions
                );
            } catch (error) {
                clearTimeout(timeoutId);
                voiceController.unsubscribeFromIncomingCallEvent(handleIncomingCall);
                worker.off("reservationCreated", handleReservationCreated);
                worker.off("reservationFailed", handleReservationFailed);
                reject(error);
            }
        });
    }

    


    #initVoiceController = async (context: ContextManager) => {
        const voiceDevice = this.#options?.voiceDevice;
        const logger = getLogger(context)("StartOutboundCall");
        const voiceController = context.getInstanceOf(VoiceControllerImpl);
        try {
            if (!voiceController.isInitialized) {
                await voiceController.init(voiceDevice);
            }
        } catch (e) {
            logger.error("Failed to initialise VoiceController", e);
        }
    };
}
